import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { BackendTokenAPI } from '../../shared/api/backend'
import * as _ from 'lodash'
import { RootState } from '../../store/store'
import { config, DashboardEventRecord, Filters, getFilters, MESSAGE } from '../Utils'

export const api = {
  async getServiceDashboardLogs(params: Filters) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    const requestBody: any = {
      log_status: params.log_status,
      start: 0,
      limit: config.ITEMS_PER_PAGE,
      order_by: params.orderBy || 'desc',
    }

    if (params.type && params.search_term) {
      requestBody.type = params.type
      requestBody.search_term = params.search_term
    }

    if (params.start) {
      requestBody.start = params.start
    }

    if (!_.isEmpty(params.range)) {
      requestBody.range = params.range
    }

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }


    if (params?.integration_partner && params.integration_partner.length) {
      requestBody.customer_portal_name = params.integration_partner
    }

    if (!_.isEmpty(params.customer_name)) {
      requestBody.customer_name = params.customer_name
    }

    if (params?.kone_category && params.kone_category.length) {
      requestBody.kone_category = params.kone_category
    }

    const response = await fetch(config.API_PATH, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()

    return {
      logs: data.data || [],
      total: data.count || 0,
    }
  },
  async getFilter(params: getFilters) {
    const isAdmin = params.org_ids && params.org_ids.length ? false : true
    const authHeader = await BackendTokenAPI.convertFrontTokenToBackendToken(
      isAdmin ? config.ADMIN_SCOPE : config.FRONTLINE_SCOPE,
    )

    // Request body according to the expected format
    const requestBody = {
      type: params.type,
    } as any

    if (!_.isEmpty(params.org_ids)) {
      requestBody.org_ids = params.org_ids
    }

    const response = await fetch(config.API_PATH + '/filter', {
      method: 'POST',
      headers: authHeader,
      body: JSON.stringify(requestBody),
    })

    if (!response.ok) {
      throw new Error(`Failed fetching the list: ${response.body ?? response.status}`)
    }

    const data = await response.json()

    return {
      filter: data.data,
    }
  },
}

export const getServiceDashboardLogs = createAsyncThunk(
  'serviceRequstDashboard/getServiceDashboardLogs',
  async (params: Filters) => {
    return await api.getServiceDashboardLogs(params)
  },
)

export const getCountryFiltersDetails = createAsyncThunk(
  'serviceRequstDashboard/getCountryFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'customer_name' })
  },
)

export const getCustomerPortalNameFiltersDetails = createAsyncThunk(
  'serviceRequstDashboard/getCustomerPortalNameFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'customer_portal_name' })
  },
)

export const getCustomerNameFiltersDetails = createAsyncThunk(
  'serviceRequstDashboard/getCustomerNameFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'customer_name' })
  },
)

export const getKoneCategoryFiltersDetails = createAsyncThunk(
  'serviceRequstDashboard/getKoneCategoryFiltersDetails',
  async (params: getFilters) => {
    return await api.getFilter({ ...params, type: 'kone_category' })
  },
)

const initialState = {
  service_dashboard_logs: [] as DashboardEventRecord[],
  loading: false,
  error: null as string | null,
  totalPage: 1,
  totalRecord: 0,
  filters: {
    log_status: MESSAGE.SUCCESS,
    type: '',
    search_term: '',
    start: 0,
    orderBy: 'desc',
  } as Filters,
  pagination: {
    currentPage: 1,
    limit: config.ITEMS_PER_PAGE,
    hasMore: true,
  },
  countries: [],
  customer_portal_names: [],
  customer_names: [],
  kone_categories: [],
}

const ServiceDashboardLogsSlice = createSlice({
  name: 'serviceRequstDashboard',
  initialState,
  reducers: {
    updateFilters(state, action) {
      state.filters = { ...state.filters, ...action.payload }
    },
    setPage(state, action) {
      if (action.payload === 'next') {
        const start = 10 * state.pagination.currentPage + 1
        state.filters.start = start === 1 ? 0 : start
        state.pagination.currentPage += 1
      } else if (action.payload === 'prev' && state.pagination.currentPage > 1) {
        state.pagination.currentPage -= 1
        state.filters.start = 10 * (state.pagination.currentPage - 1) + 1
      }
    },
    clearSearch(state) {
      state.filters.search_term = ''
      state.filters.type = ''
      state.filters.start = 0
      state.filters.range = {}
      state.pagination.currentPage = 1
      state.filters.customer_name = []
      state.filters.countries = []
      state.filters.integration_partner = []
      state.filters.kone_category = []
      state.filters.request_type = MESSAGE.INCOMING_EVENT
    },
    updatePagination(state) {
      state.pagination.currentPage = 1
      state.filters.start = 0
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceDashboardLogs.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(getServiceDashboardLogs.fulfilled, (state, action) => {
        const logs = action.payload.logs
        const total = action.payload.total
        state.loading = false
        state.service_dashboard_logs = logs || []
        state.totalPage = Math.ceil(total / 10) || 1
        state.totalRecord = total
      })
      .addCase(getServiceDashboardLogs.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message || 'Unknown error'
        state.totalPage = 1
        state.totalRecord = 0
        state.service_dashboard_logs = []
      })
      .addCase(getCustomerPortalNameFiltersDetails.pending, (state) => {
        state.customer_portal_names = []
      })
      .addCase(getCustomerPortalNameFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.customer_portal_names = filter
      })
      .addCase(getCustomerPortalNameFiltersDetails.rejected, (state, action) => {
        state.customer_portal_names = []
      })
      .addCase(getCustomerNameFiltersDetails.pending, (state) => {
        state.customer_names = []
      })
      .addCase(getCustomerNameFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.customer_names = filter
      })
      .addCase(getCustomerNameFiltersDetails.rejected, (state, action) => {
        state.customer_names = []
      })
      .addCase(getKoneCategoryFiltersDetails.pending, (state) => {
        state.kone_categories = []
      })
      .addCase(getKoneCategoryFiltersDetails.fulfilled, (state, action) => {
        const filter = action.payload.filter
        state.kone_categories = filter
      })
      .addCase(getKoneCategoryFiltersDetails.rejected, (state, action) => {
        state.kone_categories = []
      })
  },
})

export const selectCanGoPrevious = (state: RootState) => state.ServiceDashboardLogs.pagination.currentPage > 1
export const selectCanGoNext = (state: RootState) =>
  state.ServiceDashboardLogs.pagination.currentPage < state.ServiceDashboardLogs.totalPage

export const { updateFilters, setPage, clearSearch, updatePagination } = ServiceDashboardLogsSlice.actions
export default ServiceDashboardLogsSlice.reducer
